// @file Slideshow store
//
// Thin wrapper around the shared presentation store, adding slideshow-specific
// UI state (menus, panels, QR code, attachments, embeds) and backward-compatible
// page type mapping (PresentationPageType → PageType).

import { getSearchParam, isSearchParamTruthy } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { getNumberOfWords } from '@@/bits/post_processing'
import { buildSlideshowLink, getCurrentPostCid } from '@@/bits/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { usePresentationStore } from '@@/pinia/usePresentationStore'
import type { Cid } from '@@/types'
import type { Page as PresentationPage } from '@@/types/presentation'
import { PresentationPageType } from '@@/types/presentation'
import type { Page } from '@@/types/slideshow'
import { PageType } from '@@/types/slideshow'
import type { RootState } from '@@/vuexstore/surface/types'
import { LinkHelpers } from '@padlet/beethoven-client'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const PRESENTATION_TO_SLIDESHOW_PAGE_TYPE: Record<PresentationPageType, PageType> = {
  [PresentationPageType.Cover]: PageType.Cover,
  [PresentationPageType.Section]: PageType.Section,
  [PresentationPageType.Group]: PageType.Group,
  [PresentationPageType.Wish]: PageType.Post,
}

const mapPage = (page: PresentationPage): Page => ({
  ...page,
  pageType: PRESENTATION_TO_SLIDESHOW_PAGE_TYPE[page.pageType],
})

const useSlideshowStore = defineStore('slideshow', () => {
  const presentationStore = usePresentationStore()
  const surfaceStore = useSurfaceStore()
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()

  //--------------------------------------------------------------------------
  // Shared state (delegated to presentation store)
  //--------------------------------------------------------------------------

  /** @deprecated Use `usePresentationStore().hasLoadedPages` instead. */
  const hasLoadedPages = computed({
    get: () => presentationStore.hasLoadedPages,
    set: (value: boolean) => {
      presentationStore.hasLoadedPages = value
    },
  })

  /** @deprecated Use `usePresentationStore().isDataLoaded` instead. */
  const isDataLoaded = computed(() => presentationStore.isDataLoaded)

  /** @deprecated Use `usePresentationStore().currentPageIndex` instead. */
  const currentPageIndex = computed({
    get: () => presentationStore.currentPageIndex,
    set: (value: number | null) => {
      presentationStore.currentPageIndex = value
    },
  })

  /** @deprecated Use `usePresentationStore().pages` instead. */
  const pages = computed<Page[]>(() => presentationStore.pages.map(mapPage))

  /** @deprecated Use `usePresentationStore().currentPage` instead. */
  const currentPage = computed<Page | null>(() => {
    const page = presentationStore.currentPage
    return page != null ? mapPage(page) : null
  })

  /** @deprecated Use `usePresentationStore().currentPost` instead. */
  const currentPost = computed(() => presentationStore.currentPost)

  /** @deprecated Use `usePresentationStore().currentGroup` instead. */
  const currentGroup = computed(() => presentationStore.currentGroup)

  /** @deprecated Use `usePresentationStore().currentPageNumber` instead. */
  const currentPageNumber = computed(() => presentationStore.currentPageNumber)

  /** @deprecated Use `usePresentationStore().numberOfPages` instead. */
  const numberOfPages = computed(() => presentationStore.numberOfPages)

  /** @deprecated Use `usePresentationStore().lastPageIndex` instead. */
  const lastPageIndex = computed(() => presentationStore.lastPageIndex)

  /** @deprecated Use `usePresentationStore().prevPageIndex` instead. */
  const prevPageIndex = computed(() => presentationStore.prevPageIndex)

  /** @deprecated Use `usePresentationStore().nextPageIndex` instead. */
  const nextPageIndex = computed(() => presentationStore.nextPageIndex)

  /** @deprecated Use `usePresentationStore().isFirstPage` instead. */
  const isFirstPage = computed(() => presentationStore.isFirstPage)

  /** @deprecated Use `usePresentationStore().isLastPage` instead. */
  const isLastPage = computed(() => presentationStore.isLastPage)

  //--------------------------------------------------------------------------
  // Slideshow-specific: cover loading
  //--------------------------------------------------------------------------

  const pageCoverLoaded = ref(false)
  const isPageCoverLoaded = computed(() => {
    if (currentPage.value?.pageType !== PageType.Cover) return true
    if (shouldRemoveBackground.value) return true
    return pageCoverLoaded.value
  })

  /*
   * CUSTOM STYLES
   */

  const shouldRemoveBackground = computed(() => {
    const shouldRemoveBackground = getSearchParam('remove_background')
    return isSearchParamTruthy(shouldRemoveBackground)
  })

  /*
   * MENUS
   */

  const isMoreMenuOpen = ref(false)
  const isAutoplayMenuOpen = ref(false)
  const isAnyMenuOpen = computed(() => isMoreMenuOpen.value || isAutoplayMenuOpen.value)

  /*
   * PANELS
   */

  const isSharePanelOpen = ref(false)
  const isShareViaAnotherAppPanelOpen = ref(false)
  const isEmbedPanelOpen = ref(false)
  const isLanguagePanelOpen = ref(false)
  const isAccessibilitySettingsModalOpen = ref(false)
  const isCommentPanelOpen = ref(false)
  const isAnyPanelOpen = computed(
    () =>
      isSharePanelOpen.value ||
      isShareViaAnotherAppPanelOpen.value ||
      isEmbedPanelOpen.value ||
      isLanguagePanelOpen.value ||
      isAccessibilitySettingsModalOpen.value,
  )
  const isAnyOverlayOpen = computed(() => isAnyMenuOpen.value || isAnyPanelOpen.value)

  /*
   * QR CODE
   */

  const isQrCodeShown = ref(true)

  const shouldRemoveQrCode = computed(() => {
    const removeQrCodeParam = getSearchParam('remove_qr_code')
    return isSearchParamTruthy(removeQrCodeParam)
  })

  /*
   * SLIDESHOW URLS
   */

  const currentPageUrl = computed(() => {
    if (currentPage.value == null) return ''
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      return buildSlideshowLink(surfaceStore.links.show, currentPage.value.pageType, currentPage.value.entityId, {
        lang: surfaceTranslationsStore.currentLanguage,
      })
    }
    return buildSlideshowLink(surfaceStore.links.show, currentPage.value.pageType, currentPage.value.entityId)
  })
  const coverPageUrl = computed(() => {
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      return buildSlideshowLink(surfaceStore.links.show, PageType.Cover, undefined, {
        lang: surfaceTranslationsStore.currentLanguage,
      })
    }
    return buildSlideshowLink(surfaceStore.links.show, PageType.Cover)
  })

  const numberOfWordsOnPage = computed(() => {
    if (currentPage.value?.pageType === PageType.Cover) {
      return getNumberOfWords(surfaceStore.title) + getNumberOfWords(surfaceStore.description)
    } else if (
      (currentPage.value?.pageType === PageType.Section || currentPage.value?.pageType === PageType.Group) &&
      currentPage.value?.entityId !== undefined
    ) {
      if (currentGroup.value == null) {
        return 0
      }

      if (!('title' in currentGroup.value) || currentGroup.value?.title == null) return 0
      return getNumberOfWords(currentGroup.value.title)
    }

    if (currentPost.value != null) {
      return getNumberOfWords(currentPost.value.body) + getNumberOfWords(currentPost.value.subject)
    }

    return 0
  })

  /*
   * ATTACHMENTS
   */

  const postCidsForLoadedAttachments = ref(new Set<Cid>())
  const areAttachmentsLoadedForCurrentPost = computed(() => {
    if (
      currentPage.value?.pageType === PageType.Post &&
      currentPage.value?.entityId != null &&
      currentPost.value?.attachment != null &&
      currentPost.value?.attachment.length > 0
    ) {
      const currentPostCid = getCurrentPostCid(currentPage.value.entityId)
      return postCidsForLoadedAttachments.value.has(currentPostCid)
    }
    return true
  })
  const numberOfAttachmentsOnPage = computed(() => {
    if (
      currentPage.value?.pageType !== PageType.Post ||
      currentPost.value?.attachment === undefined ||
      currentPost.value?.attachment === ''
    ) {
      return 0
    }
    // at the moment we only support 1 attachment per post
    return 1
  })

  /*
   * Embed
   */

  const slideshowEmbedCodeUrlTemplate = computed<string>(
    () => getVuexStore<RootState>()?.state.constants.slideshowEmbedCodeUrlTemplate,
  )

  const currentSlideHasPadletEmbed = computed(() => {
    if (
      currentPage.value?.pageType !== PageType.Post ||
      currentPost.value == null ||
      isEmpty(currentPost.value.attachment)
    ) {
      return false
    }
    const displayAttributes = surfaceAttachmentsStore.getLinkDisplayAttributesForPost(currentPost.value.cid)
    return displayAttributes != null && LinkHelpers.isPadletWall(displayAttributes)
  })

  return {
    // Data
    isDataLoaded,
    hasLoadedPages,
    pageCoverLoaded,
    isPageCoverLoaded,

    // Custom styles
    shouldRemoveBackground,

    // Overlays
    isAnyMenuOpen,
    isAnyOverlayOpen,
    isMoreMenuOpen,
    isSharePanelOpen,
    isAutoplayMenuOpen,
    isShareViaAnotherAppPanelOpen,
    isEmbedPanelOpen,
    isLanguagePanelOpen,
    isAccessibilitySettingsModalOpen,
    isCommentPanelOpen,

    // QR Code
    isQrCodeShown,
    shouldRemoveQrCode,

    // Pages
    pages,
    currentPageIndex,
    currentPost,
    currentGroup,
    currentPage,
    currentPageNumber,
    lastPageIndex,
    prevPageIndex,
    nextPageIndex,
    isFirstPage,
    isLastPage,
    numberOfPages,
    numberOfWordsOnPage,
    currentPageUrl,
    coverPageUrl,

    // Attachments
    postCidsForLoadedAttachments,
    areAttachmentsLoadedForCurrentPost,
    numberOfAttachmentsOnPage,

    // Embed
    slideshowEmbedCodeUrlTemplate,
    currentSlideHasPadletEmbed,
  }
})
export { useSlideshowStore }
